import type { TFunction } from 'i18next';
import pLimit from 'p-limit';

import type {
    CreateFileTransferRequestResponse,
    UpdateFileTransferRequestPayload,
} from '../../../types/StudioFileManager';
import type { FileTransferServiceClient } from './proto/FileTransferServiceClient';
import {
    ChunkDescriptor,
    UPLOAD_CHUNK_CONCURRENCY,
    UPLOAD_CHUNK_RETRIES,
    UPLOAD_CHUNK_SIZE_BYTES,
    UploadItem,
    UploadStatus,
} from './grpcFileManagerTypes';

export type UploadItemsUpdater = (prev: UploadItem[]) => UploadItem[];

export function patchUploadItem(
    items: UploadItem[],
    clientId: string,
    patch: Partial<UploadItem> | ((item: UploadItem) => UploadItem)
): UploadItem[] {
    return items.map((ui) => {
        if (ui.clientId !== clientId) {
            return ui;
        }
        return typeof patch === 'function' ? patch(ui) : { ...ui, ...patch };
    });
}

export function mergeRetryItemsIntoUploadList(prev: UploadItem[], pendingItems: UploadItem[]): UploadItem[] {
    const pendingByClientId = new Map(pendingItems.map((item) => [item.clientId, item]));
    return prev.map((ui) => pendingByClientId.get(ui.clientId) ?? ui);
}

export function filterPendingUploadItems(source: UploadItem[]): UploadItem[] {
    return source.filter(
        (item) =>
            (item.status === UploadStatus.Pending || item.status === UploadStatus.Failed) &&
            !!item.storedFilename?.trim() &&
            item.fileTransferRequestDetailId != null
    );
}

export function prepareFailedItemsForRetry(failedItems: UploadItem[]): UploadItem[] {
    return failedItems
        .filter((item) => item.fileTransferRequestDetailId != null && !!item.storedFilename?.trim())
        .map((item) => ({
            ...item,
            status: UploadStatus.Pending,
            abortController: new AbortController(),
            errorMessage: '',
            bytesSent: 0,
            progress: 0,
            currentAttempt: 0,
        }));
}

export function buildChunkDescriptors(file: File): ChunkDescriptor[] {
    const descriptors: ChunkDescriptor[] = [];
    if (file.size === 0) {
        descriptors.push({ index: 0, blob: file.slice(0, 0) });
        return descriptors;
    }

    for (let offset = 0, idx = 0; offset < file.size; offset += UPLOAD_CHUNK_SIZE_BYTES, idx++) {
        descriptors.push({
            index: idx,
            blob: file.slice(offset, Math.min(offset + UPLOAD_CHUNK_SIZE_BYTES, file.size)),
        });
    }
    return descriptors;
}

export function isIdempotentCompletion(message?: string): boolean {
    return typeof message === 'string' && message.toLowerCase().includes('already finalized');
}

export function delayMs(ms: number): Promise<void> {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}

export function applyDevFailureSimulationFirstRound(
    pendingItems: UploadItem[],
    devSimulatedFailureFileNames: Set<string>
): void {
    devSimulatedFailureFileNames.clear();
    if (!import.meta.env.DEV) {
        return;
    }

    const n = Number.parseInt(localStorage.getItem('STUDIO_SIMULATE_UPLOAD_FAILURES') || '0', 10);
    if (!Number.isFinite(n) || n <= 0) {
        return;
    }

    const count = Math.min(n, pendingItems.length);
    pendingItems.slice(0, count).forEach((item) => {
        devSimulatedFailureFileNames.add(item.fileName);
    });
    console.info(
        `[GRPCFileManager] Simulating failure for ${count} file(s). Clear: localStorage.removeItem("STUDIO_SIMULATE_UPLOAD_FAILURES")`
    );
}

export function getItemsForFileTransferRequest(requestId: number, currentItems: UploadItem[]): UploadItem[] {
    return currentItems.filter((item) => item.fileTransferRequestId === requestId);
}

export function isServerSyncedUploadComplete(requestId: number, currentItems: UploadItem[]): boolean {
    const itemsForRequest = getItemsForFileTransferRequest(requestId, currentItems);
    return (
        requestId > 0 &&
        itemsForRequest.length > 0 &&
        itemsForRequest.every(
            (item) => item.fileTransferRequestDetailId !== null && item.status === UploadStatus.Completed
        )
    );
}

export function buildSyncedUpdateResult(requestId: number, currentItems: UploadItem[]) {
    const itemsForRequest = getItemsForFileTransferRequest(requestId, currentItems);
    const total = itemsForRequest.length;
    const ok = itemsForRequest.filter((item) => item.status === UploadStatus.Completed).length;
    return {
        isSuccess: true,
        fileTransferRequestId: requestId,
        totalFiles: total,
        successfulFiles: ok,
        failedFiles: total - ok,
        isCompleted: ok === total,
    };
}

type ChunkUploadContext = {
    item: UploadItem;
    uploadFileName: string;
    s3Key: string;
    totalChunks: number;
    grpcClient: FileTransferServiceClient;
    signal: AbortSignal | undefined;
    getUsername: () => string | null;
    t: TFunction;
    attemptNumber: number;
    bumpProgress: (delta: number) => Promise<void>;
    onChunkResult: (result: { fileUploadPath?: string; message?: string; completed?: boolean }) => void;
    sawCompletedRef: { current: boolean };
};

async function uploadSingleChunk(ctx: ChunkUploadContext, descriptor: ChunkDescriptor): Promise<void> {
    let lastErr: unknown;
    for (let attempt = 1; attempt <= UPLOAD_CHUNK_RETRIES; attempt++) {
        try {
            const arrayBuffer = await descriptor.blob.arrayBuffer();
            const uint8Array = new Uint8Array(arrayBuffer);
            const isLastChunk = descriptor.index === ctx.totalChunks - 1;
            const result = await ctx.grpcClient.uploadChunk(
                {
                    fileName: ctx.uploadFileName,
                    chunkIndex: descriptor.index,
                    data: uint8Array,
                    isLastChunk,
                    key: ctx.s3Key,
                    totalChunks: ctx.totalChunks,
                    fileTransferRequestId: ctx.item.fileTransferRequestId ?? undefined,
                    fileTransferRequestDetailId: ctx.item.fileTransferRequestDetailId ?? undefined,
                    userName: ctx.getUsername() || undefined,
                },
                ctx.signal
            );
            await ctx.bumpProgress(uint8Array.byteLength);
            ctx.onChunkResult(result);
            if (result.completed || isIdempotentCompletion(result.message)) {
                ctx.sawCompletedRef.current = true;
            }
            return;
        } catch (error) {
            lastErr = error;
            if (ctx.signal?.aborted) {
                throw error;
            }
            if (ctx.sawCompletedRef.current) {
                return;
            }
            await delayMs(400 * attempt);
        }
    }

    if (ctx.sawCompletedRef.current) {
        return;
    }

    const detail = lastErr instanceof Error ? lastErr.message : String(lastErr ?? 'unknown');
    console.error('[GRPCFileManager] Chunk upload failed', {
        fileName: ctx.item.fileName,
        chunkIndex: descriptor.index,
        totalChunks: ctx.totalChunks,
        attempt: ctx.attemptNumber,
        detail,
    });
    throw new Error(
        ctx.t('studio.upload.chunkFailed', {
            chunk: descriptor.index + 1,
            totalChunks: ctx.totalChunks,
            detail,
        })
    );
}

async function uploadChunkBatch(ctx: ChunkUploadContext, descriptors: ChunkDescriptor[]): Promise<void> {
    const nonLast = descriptors.filter((d) => d.index !== ctx.totalChunks - 1);
    const last = descriptors.find((d) => d.index === ctx.totalChunks - 1);
    const limit = pLimit(UPLOAD_CHUNK_CONCURRENCY);

    try {
        await Promise.all(nonLast.map((descriptor) => limit(() => uploadSingleChunk(ctx, descriptor))));
        if (last) {
            await uploadSingleChunk(ctx, last);
        }
    } catch (chunkBatchError) {
        if (!ctx.sawCompletedRef.current) {
            throw chunkBatchError;
        }
        console.warn('[GRPCFileManager] Ignoring late chunk error after successful finalize', {
            fileName: ctx.item.fileName,
            detail: (chunkBatchError as Error)?.message,
        });
    }
}

export type ExecuteUploadAttemptParams = {
    item: UploadItem;
    attemptNumber: number;
    s3DestinationPath: string | undefined;
    grpcClient: FileTransferServiceClient;
    getUsername: () => string | null;
    t: TFunction;
    resolveUploadFileName: (item: Pick<UploadItem, 'fileName' | 'storedFilename'>) => string;
    buildS3ObjectKey: (s3DestinationPath: string | undefined, fileName: string) => string;
    updateUploadItems: (updater: UploadItemsUpdater) => void;
    devSimulatedFailureFileNames: Set<string>;
    batchUploadCounts: { ok: number; fail: number };
};

export async function executeUploadAttempt(params: ExecuteUploadAttemptParams): Promise<void> {
    const {
        item,
        attemptNumber,
        s3DestinationPath,
        grpcClient,
        getUsername,
        t,
        resolveUploadFileName,
        buildS3ObjectKey,
        updateUploadItems,
        devSimulatedFailureFileNames,
        batchUploadCounts,
    } = params;

    updateUploadItems((prev) =>
        patchUploadItem(prev, item.clientId, {
            status: UploadStatus.Uploading,
            errorMessage:
                attemptNumber > 1
                    ? t('studio.upload.retryingAfterError', {
                          attempt: attemptNumber,
                          maxAttempts: item.maxAttempts,
                      })
                    : '',
            bytesSent: 0,
            progress: 0,
            currentAttempt: attemptNumber,
        })
    );

    const file = item.file;
    const signal = item.abortController?.signal;
    const uploadFileName = resolveUploadFileName(item);
    const s3Key = buildS3ObjectKey(s3DestinationPath, uploadFileName);
    const descriptors = buildChunkDescriptors(file);
    const totalChunks = descriptors.length;

    let lastPath = '';
    let lastServerMessage = '';
    let sentAccumulator = 0;
    let progressChain: Promise<void> = Promise.resolve();
    const sawCompletedRef = { current: false };

    const bumpProgress = (delta: number): Promise<void> => {
        progressChain = progressChain.then(() => {
            sentAccumulator += delta;
            const progress =
                item.totalSize > 0 ? Math.min(100, Math.round((sentAccumulator / item.totalSize) * 100)) : 100;
            updateUploadItems((prev) =>
                patchUploadItem(prev, item.clientId, {
                    bytesSent: Math.min(sentAccumulator, item.totalSize),
                    progress,
                })
            );
        });
        return progressChain;
    };

    const chunkCtx: ChunkUploadContext = {
        item,
        uploadFileName,
        s3Key,
        totalChunks,
        grpcClient,
        signal,
        getUsername,
        t,
        attemptNumber,
        bumpProgress,
        sawCompletedRef,
        onChunkResult: (result) => {
            if (result.fileUploadPath) {
                lastPath = result.fileUploadPath;
            }
            if (result.message) {
                lastServerMessage = result.message;
            }
        },
    };

    await uploadChunkBatch(chunkCtx, descriptors);

    if (!sawCompletedRef.current) {
        console.error('[GRPCFileManager] Upload bytes sent but server did not confirm completion', {
            fileName: item.fileName,
            s3Key,
            lastPath,
            lastServerMessage,
            totalChunks,
            bytesSent: sentAccumulator,
            attempt: attemptNumber,
        });
        throw new Error(
            t('studio.upload.completionNotConfirmed', {
                detail: lastServerMessage || t('studio.upload.noServerCompletionDetail'),
            })
        );
    }

    if (devSimulatedFailureFileNames.has(item.fileName)) {
        devSimulatedFailureFileNames.delete(item.fileName);
        batchUploadCounts.fail += 1;
        updateUploadItems((prev) =>
            patchUploadItem(prev, item.clientId, {
                bytesSent: 0,
                progress: 0,
                status: UploadStatus.Failed,
                errorMessage: t('studio.upload.simulatedFailureDev'),
            })
        );
        return;
    }

    batchUploadCounts.ok += 1;
    updateUploadItems((prev) =>
        patchUploadItem(prev, item.clientId, {
            bytesSent: item.totalSize,
            progress: 100,
            fileUploadPath: lastPath || s3Key,
            status: UploadStatus.Completed,
        })
    );
}

export function buildUpdateFileTransferPayload(
    requestId: number,
    currentItems: UploadItem[]
): UpdateFileTransferRequestPayload {
    const itemsForRequest = getItemsForFileTransferRequest(requestId, currentItems);
    return {
        fileTransferRequestId: requestId,
        fileDetails: itemsForRequest
            .filter((item) => item.fileTransferRequestDetailId !== null)
            .map((item) => ({
                fileTransferRequestDetailId: item.fileTransferRequestDetailId!,
                isSuccess: item.status === UploadStatus.Completed,
                FileUploadPath: item.fileUploadPath || '',
            })),
    };
}

export function countCreateValidationResults(
    withTransferContext: UploadItem[],
    response: CreateFileTransferRequestResponse
) {
    const acceptedCount =
        response.acceptedFileCount ??
        response.fileDetails?.length ??
        withTransferContext.filter((item) => !!item.storedFilename).length;
    const rejectedCount = withTransferContext.filter(
        (item) => item.status === UploadStatus.Failed && !item.storedFilename
    ).length;
    return { acceptedCount, rejectedCount };
}

export function buildCreateValidationFailureResult(
    response: CreateFileTransferRequestResponse,
    withTransferContext: UploadItem[],
    rejectedCount: number
) {
    return {
        isSuccess: false,
        fileTransferRequestId: response.fileTransferRequestId,
        totalFiles: withTransferContext.length,
        successfulFiles: 0,
        failedFiles: rejectedCount || withTransferContext.length,
        isCompleted: false,
    };
}

export function buildPartialUploadFailureResult(
    response: CreateFileTransferRequestResponse,
    withTransferContext: UploadItem[]
) {
    return {
        isSuccess: false,
        fileTransferRequestId: response.fileTransferRequestId,
        totalFiles: withTransferContext.length,
        successfulFiles: withTransferContext.filter((item) => item.status === UploadStatus.Completed).length,
        failedFiles: withTransferContext.filter((item) => item.status === UploadStatus.Failed).length,
        isCompleted: false,
    };
}

function normalizeUploadFileName(fileName: string): string {
    const trimmed = (fileName || '').trim().replace(/\\/g, '/');
    const name = trimmed.includes('/') ? trimmed.slice(trimmed.lastIndexOf('/') + 1) : trimmed;
    const lastDot = name.lastIndexOf('.');
    if (lastDot <= 0) {
        // sonar-disable-next-line S5852
        return name.replace(/\s+$/u, '');
    }
    // sonar-disable-next-line S5852
    const base = name.slice(0, lastDot).replace(/\s+$/u, '');
    const ext = name.slice(lastDot);
    return `${base}${ext}`;
}

export function takeFromQueueMap<T>(queueMap: Map<string, T[]>, key: string): T | undefined {
    const queue = queueMap.get(key);
    if (!queue || queue.length === 0) {
        return undefined;
    }
    return queue.shift();
}

export function buildCreateResponseQueueMaps(response: CreateFileTransferRequestResponse): {
    failedQueues: Map<string, NonNullable<CreateFileTransferRequestResponse['failedFiles']>>;
    detailQueues: Map<string, NonNullable<CreateFileTransferRequestResponse['fileDetails']>>;
} {
    const failedQueues = new Map<string, NonNullable<CreateFileTransferRequestResponse['failedFiles']>>();
    for (const failedFile of response.failedFiles ?? []) {
        const key = normalizeUploadFileName(failedFile.originalFilename || '').toLowerCase();
        const queue = failedQueues.get(key) ?? [];
        queue.push(failedFile);
        failedQueues.set(key, queue);
    }

    const detailQueues = new Map<string, NonNullable<CreateFileTransferRequestResponse['fileDetails']>>();
    for (const detail of response.fileDetails ?? []) {
        if (!detail.originalFilename) {
            continue;
        }
        const key = normalizeUploadFileName(detail.originalFilename).toLowerCase();
        const queue = detailQueues.get(key) ?? [];
        queue.push(detail);
        detailQueues.set(key, queue);
    }

    return { failedQueues, detailQueues };
}

export function mapUploadItemsWithTransferContext(
    uploadItems: UploadItem[],
    response: CreateFileTransferRequestResponse,
    t: TFunction
): UploadItem[] {
    const { failedQueues, detailQueues } = buildCreateResponseQueueMaps(response);

    return uploadItems.map((item) => {
        if (item.status === UploadStatus.Failed && !item.fileTransferRequestDetailId) {
            return {
                ...item,
                fileTransferRequestId: response.fileTransferRequestId,
            };
        }

        const key = normalizeUploadFileName(item.fileName).toLowerCase();
        const detail = takeFromQueueMap(detailQueues, key);
        if (detail) {
            return {
                ...item,
                fileTransferRequestId: response.fileTransferRequestId,
                fileTransferRequestDetailId: detail.fileTransferRequestDetailId ?? null,
                storedFilename: detail.storedFilename?.trim() || item.storedFilename,
            };
        }

        const failed = takeFromQueueMap(failedQueues, key);
        if (failed) {
            const reason = failed.reason || t('studio.upload.uploadError');
            const withBarcode = failed.barcode
                ? `${reason} (${t('studio.upload.createFailBarcode')}: ${failed.barcode})`
                : reason;
            return {
                ...item,
                fileTransferRequestId: response.fileTransferRequestId,
                fileTransferRequestDetailId: null,
                storedFilename: undefined,
                status: UploadStatus.Failed,
                errorMessage: withBarcode,
                progress: 0,
            };
        }

        return {
            ...item,
            fileTransferRequestId: response.fileTransferRequestId,
            fileTransferRequestDetailId: null,
            storedFilename: undefined,
            status: UploadStatus.Failed,
            errorMessage: t('studio.upload.createMatchMissing'),
            progress: 0,
        };
    });
}

export function applyCreateChunkToItems(
    allItems: UploadItem[],
    chunkItems: UploadItem[],
    response: CreateFileTransferRequestResponse,
    t: TFunction,
    options?: { resetFailedMatchToPending?: boolean }
): UploadItem[] {
    const chunkClientIds = new Set(chunkItems.map((item) => item.clientId));
    const resetFailedMatchToPending = options?.resetFailedMatchToPending === true;
    const { failedQueues, detailQueues } = buildCreateResponseQueueMaps(response);

    return allItems.map((item) => {
        if (!chunkClientIds.has(item.clientId)) {
            return item;
        }

        if (item.status === UploadStatus.Failed && !item.storedFilename && !resetFailedMatchToPending) {
            return {
                ...item,
                fileTransferRequestId: response.fileTransferRequestId,
            };
        }

        const key = normalizeUploadFileName(item.fileName).toLowerCase();
        const detail = takeFromQueueMap(detailQueues, key);
        if (detail) {
            return {
                ...item,
                fileTransferRequestId: response.fileTransferRequestId,
                fileTransferRequestDetailId: detail.fileTransferRequestDetailId ?? null,
                storedFilename: detail.storedFilename?.trim() || item.storedFilename,
                ...(resetFailedMatchToPending ? { status: UploadStatus.Pending, errorMessage: '', progress: 0 } : {}),
            };
        }

        const failed = takeFromQueueMap(failedQueues, key);
        if (failed) {
            const reason = failed.reason || t('studio.upload.uploadError');
            const withBarcode = failed.barcode
                ? `${reason} (${t('studio.upload.createFailBarcode')}: ${failed.barcode})`
                : reason;
            return {
                ...item,
                fileTransferRequestId: response.fileTransferRequestId,
                fileTransferRequestDetailId: null,
                storedFilename: undefined,
                status: UploadStatus.Failed,
                errorMessage: withBarcode,
                progress: 0,
            };
        }

        return {
            ...item,
            fileTransferRequestId: response.fileTransferRequestId,
            fileTransferRequestDetailId: null,
            storedFilename: undefined,
            status: UploadStatus.Failed,
            errorMessage: t('studio.upload.createMatchMissing'),
            progress: 0,
        };
    });
}

export function mapMatchFailedItemsAfterRetry(
    uploadItems: UploadItem[],
    response: CreateFileTransferRequestResponse,
    t: TFunction
): UploadItem[] {
    const { failedQueues, detailQueues } = buildCreateResponseQueueMaps(response);

    return uploadItems.map((item) => {
        if (item.status !== UploadStatus.Failed || !!item.storedFilename) {
            return item;
        }

        const key = normalizeUploadFileName(item.fileName).toLowerCase();
        const detail = takeFromQueueMap(detailQueues, key);
        if (detail) {
            return {
                ...item,
                fileTransferRequestId: response.fileTransferRequestId,
                fileTransferRequestDetailId: detail.fileTransferRequestDetailId ?? null,
                storedFilename: detail.storedFilename?.trim() || item.storedFilename,
                status: UploadStatus.Pending,
                errorMessage: '',
                progress: 0,
            };
        }

        const failed = takeFromQueueMap(failedQueues, key);
        if (failed) {
            const reason = failed.reason || t('studio.upload.uploadError');
            const withBarcode = failed.barcode
                ? `${reason} (${t('studio.upload.createFailBarcode')}: ${failed.barcode})`
                : reason;
            return {
                ...item,
                fileTransferRequestId: response.fileTransferRequestId,
                status: UploadStatus.Failed,
                errorMessage: withBarcode,
                progress: 0,
            };
        }

        return {
            ...item,
            fileTransferRequestId: response.fileTransferRequestId,
            status: UploadStatus.Failed,
            errorMessage: t('studio.upload.createMatchMissing'),
            progress: 0,
        };
    });
}

export function handleUploadAttemptError(
    error: unknown,
    params: {
        item: UploadItem;
        attemptNumber: number;
        t: TFunction;
        updateUploadItems: (updater: UploadItemsUpdater) => void;
        batchUploadCounts: { ok: number; fail: number };
        retryAttempt: (nextAttempt: number) => Promise<void>;
    }
): Promise<void> {
    const { item, attemptNumber, t, updateUploadItems, batchUploadCounts, retryAttempt } = params;

    if (error instanceof Error && error.name === 'AbortError') {
        batchUploadCounts.fail += 1;
        updateUploadItems((prev) => patchUploadItem(prev, item.clientId, { status: UploadStatus.Cancelled }));
        return Promise.resolve();
    }

    const failureDetail = error instanceof Error ? error.message : t('studio.upload.uploadFailed');
    console.error('[GRPCFileManager] File upload attempt failed', {
        fileName: item.fileName,
        attempt: attemptNumber,
        maxAttempts: item.maxAttempts,
        detail: failureDetail,
    });

    updateUploadItems((prev) => patchUploadItem(prev, item.clientId, { errorMessage: failureDetail }));

    if (attemptNumber < item.maxAttempts) {
        return delayMs(1000).then(() => retryAttempt(attemptNumber + 1));
    }

    batchUploadCounts.fail += 1;
    updateUploadItems((prev) =>
        patchUploadItem(prev, item.clientId, {
            status: UploadStatus.Failed,
            errorMessage: `${failureDetail} (${item.maxAttempts} ${t('studio.upload.attemptsText')})`,
        })
    );
    return Promise.resolve();
}

/** Mutates `items` / winner map: keep clean name over spaced duplicate. */
export function registerUploadItemWithDuplicateCheck(
    items: UploadItem[],
    winnerIndexByNormalized: Map<string, number>,
    item: UploadItem,
    duplicateMessage: string
): void {
    const normalizedKey = item.fileName.toLowerCase();
    const newIsClean = item.rawFileName === item.fileName;
    const existingIdx = winnerIndexByNormalized.get(normalizedKey);

    if (existingIdx === undefined) {
        winnerIndexByNormalized.set(normalizedKey, items.length);
        items.push(item);
        return;
    }

    const existing = items[existingIdx];
    const existingIsClean = existing.rawFileName === existing.fileName;
    if (newIsClean && !existingIsClean) {
        existing.status = UploadStatus.Failed;
        existing.errorMessage = duplicateMessage;
        winnerIndexByNormalized.set(normalizedKey, items.length);
    } else {
        item.status = UploadStatus.Failed;
        item.errorMessage = duplicateMessage;
    }
    items.push(item);
}

export type UploadSingleFileParams = Omit<ExecuteUploadAttemptParams, 'attemptNumber'> & {
    missingDetailMessage: string;
};

export async function uploadSingleFileWithRetries(params: UploadSingleFileParams): Promise<void> {
    const { item, missingDetailMessage, updateUploadItems, t } = params;

    if (item.fileTransferRequestDetailId == null) {
        updateUploadItems((prev) =>
            patchUploadItem(prev, item.clientId, (ui) => ({
                ...ui,
                status: UploadStatus.Failed,
                errorMessage: ui.errorMessage || missingDetailMessage,
            }))
        );
        return;
    }

    const attemptUpload = async (attemptNumber: number): Promise<void> => {
        try {
            await executeUploadAttempt({ ...params, attemptNumber });
        } catch (error: unknown) {
            await handleUploadAttemptError(error, {
                item,
                attemptNumber,
                t,
                updateUploadItems,
                batchUploadCounts: params.batchUploadCounts,
                retryAttempt: attemptUpload,
            });
        }
    };

    await attemptUpload(1);
}

export async function runParallelUploadQueue(params: {
    allItems: UploadItem[];
    maxParallel: number;
    uploadOne: (item: UploadItem) => Promise<void>;
}): Promise<void> {
    const { allItems, maxParallel, uploadOne } = params;
    const activeUploads = new Set<Promise<void>>();
    let itemIndex = 0;

    const startNextUpload = async (): Promise<void> => {
        if (itemIndex >= allItems.length) {
            return;
        }

        const item = allItems[itemIndex++];
        const uploadPromise = uploadOne(item);
        activeUploads.add(uploadPromise);

        try {
            await uploadPromise;
        } catch {
            // Errors are handled within uploadOne
        } finally {
            activeUploads.delete(uploadPromise);
            if (itemIndex < allItems.length) {
                startNextUpload();
            }
        }
    };

    const initial = Math.min(maxParallel, allItems.length);
    for (let i = 0; i < initial; i++) {
        startNextUpload();
    }

    while (activeUploads.size > 0) {
        await Promise.race(Array.from(activeUploads));
    }
}

export function buildBatchUploadStatusMessage(ok: number, fail: number, t: TFunction): string {
    if (fail > 0 && ok > 0) {
        return t('studio.upload.partialUploadSummary', { success: ok, failed: fail });
    }
    if (fail > 0 && ok === 0) {
        return t('studio.upload.allUploadsFailed');
    }
    return t('studio.upload.allUploadsCompleted');
}
